import React, { useState, useEffect } from 'react';
import { X, Shield, Cookie, Settings, Check } from 'lucide-react';

const CookieSettings = ({ isOpen, onClose, onSave }) => {
    const [settings, setSettings] = useState({
        necessary: true, // Always true, cannot be disabled
        functional: true,
        analytics: true,
        marketing: false
    });

    useEffect(() => {
        // Load existing settings
        const savedSettings = localStorage.getItem('cookieSettings');
        if (savedSettings) {
            try {
                const parsed = JSON.parse(savedSettings);
                setSettings(prev => ({ ...prev, ...parsed }));
            } catch (e) {
                console.error('Error parsing cookie settings:', e);
            }
        }
    }, []);

    const handleToggle = (type) => {
        if (type === 'necessary') return; // Cannot disable necessary cookies

        setSettings(prev => ({
            ...prev,
            [type]: !prev[type]
        }));
    };

    const handleSave = () => {
        localStorage.setItem('cookieSettings', JSON.stringify(settings));
        localStorage.setItem('cookieConsent', 'customized');
        localStorage.setItem('cookieConsentDate', new Date().toISOString());
        onSave(settings);
        onClose();
    };

    const handleAcceptAll = () => {
        const allAccepted = {
            necessary: true,
            functional: true,
            analytics: true,
            marketing: true
        };
        setSettings(allAccepted);
        localStorage.setItem('cookieSettings', JSON.stringify(allAccepted));
        localStorage.setItem('cookieConsent', 'accepted');
        localStorage.setItem('cookieConsentDate', new Date().toISOString());
        onSave(allAccepted);
        onClose();
    };

    const handleRejectAll = () => {
        const onlyNecessary = {
            necessary: true,
            functional: false,
            analytics: false,
            marketing: false
        };
        setSettings(onlyNecessary);
        localStorage.setItem('cookieSettings', JSON.stringify(onlyNecessary));
        localStorage.setItem('cookieConsent', 'declined');
        localStorage.setItem('cookieConsentDate', new Date().toISOString());
        onSave(onlyNecessary);
        onClose();
    };

    if (!isOpen) return null;

    const cookieTypes = [
        {
            key: 'necessary',
            title: 'Необходимые',
            description: 'Эти файлы cookie необходимы для работы сайта и не могут быть отключены.',
            examples: 'Аутентификация, безопасность, сохранение настроек'
        },
        {
            key: 'functional',
            title: 'Функциональные',
            description: 'Позволяют сайту запоминать ваши предпочтения и улучшить ваш опыт.',
            examples: 'Язык интерфейса, тема оформления, размер текста'
        },
        {
            key: 'analytics',
            title: 'Аналитические',
            description: 'Помогают нам понять, как посетители взаимодействуют с сайтом.',
            examples: 'Статистика посещений, популярные страницы, время на сайте'
        },
        {
            key: 'marketing',
            title: 'Маркетинговые',
            description: 'Используются для персонализации рекламы и отслеживания эффективности кампаний.',
            examples: 'Реклама в социальных сетях, ретаргетинг, конверсии'
        }
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-2xl">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-violet-600 rounded-full flex items-center justify-center">
                                <Settings className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900">
                                Настройки cookie
                            </h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-gray-600 mb-6">
                        Управляйте настройками cookie, чтобы контролировать, какую информацию мы можем собирать.
                        Вы можете изменить эти настройки в любое время.
                    </p>

                    <div className="space-y-6">
                        {cookieTypes.map((type) => (
                            <div key={type.key} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <h3 className="font-medium text-gray-900">{type.title}</h3>
                                            {type.key === 'necessary' && (
                                                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                                                    Обязательные
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-600 mb-2">{type.description}</p>
                                        <p className="text-xs text-gray-500">
                                            <strong>Примеры:</strong> {type.examples}
                                        </p>
                                    </div>

                                    <div className="ml-4">
                                        <button
                                            onClick={() => handleToggle(type.key)}
                                            disabled={type.key === 'necessary'}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${settings[type.key]
                                                    ? 'bg-purple-600'
                                                    : 'bg-gray-200'
                                                } ${type.key === 'necessary' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                        >
                                            <span
                                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings[type.key] ? 'translate-x-6' : 'translate-x-1'
                                                    }`}
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Privacy Policy Link */}
                    <div className="mt-6 p-4 bg-purple-50 rounded-lg">
                        <div className="flex items-center space-x-2">
                            <Shield className="w-5 h-5 text-purple-600" />
                            <p className="text-sm text-gray-700">
                                Подробнее о том, как мы обрабатываем ваши данные, читайте в нашей{' '}
                                <a
                                    href="/pages/politikakonfidenzialnosti"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-purple-600 hover:text-purple-800 font-medium underline"
                                >
                                    Политике конфиденциальности
                                </a>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 rounded-b-2xl">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={handleRejectAll}
                            className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Только необходимые
                        </button>
                        <button
                            onClick={handleSave}
                            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center"
                        >
                            <Check className="w-4 h-4 mr-2" />
                            Сохранить настройки
                        </button>
                        <button
                            onClick={handleAcceptAll}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-lg hover:from-purple-700 hover:to-violet-700 transition-all"
                        >
                            Принять все
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CookieSettings;